import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Box, BoxProps, Button, Center, Flex, Text, VStack } from '@chakra-ui/react';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { CommentCard, CommentCardProps } from '@src/components/sco/Comments/CommentCard';
import { CommentComposer } from '@src/components/sco/Comments/CommentComposer';
import { PaywalledAction, PaywalledActionProps } from '@src/components/sco/PaywalledAction';
import { QueryContainer } from '@src/components/sco/QueryContainer';
import { ScrollBoxWithShadows } from '@src/components/sco/ScrollBoxWithShadows';
import { Forum } from '@src/graphql-types';
import { gql } from '@src/lib/gql';
import { ReactNode, useEffect, useState } from 'react';
export type CommentListProps = {
  attachmentType?: string;
  bgColor?: BoxProps['bgColor'];
  collapseComments?: boolean;
  composerBoxProps?: BoxProps;
  commentCardProps?: Partial<CommentCardProps>;
  innerPadding?: BoxProps['padding'];
  isScrollable?: boolean;
  invertOrder?: boolean;
  maxHeight?: BoxProps['height'];
  order?: [string[]];
  parentCommentId?: string;
  showBottomComposer?: boolean;
  showTopComposer?: boolean;
  showFixedComposer?: boolean;
  showReplies?: boolean;
  threadId?: string;
  renderBlankState?: () => ReactNode;
  composerPlaceholder?: string;
  refetchTrigger?: any;
  afterRefetch?: () => void;
  paywalledActionProps?: PaywalledActionProps;
};
export const CommentList = props => {
  const $ = _c(60);
  const [showTopComposer] = useState(props.showTopComposer);
  const [showBottomComposer] = useState(props.showBottomComposer);
  const [showFixedComposer] = useState(props.showFixedComposer);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = CommentList.queries.comments();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props.order) {
    t1 = props.order || [["featuredAt", "DESC"], ["createdAt", "DESC"]];
    $[1] = props.order;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = props.parentCommentId || null;
  const t3 = props.threadId || null;
  let t4;
  if ($[3] !== t1 || $[4] !== t2 || $[5] !== t3) {
    t4 = {
      variables: {
        order: t1,
        pageSize: 50,
        parentCommentId: t2,
        threadId: t3
      }
    };
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const query = useQuery(t0, t4);
  let t5;
  if ($[7] !== props || $[8] !== query) {
    t5 = () => {
      if (props.refetchTrigger) {
        query.refetch().then(() => props.afterRefetch());
      }
    };
    $[7] = props;
    $[8] = query;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== props.refetchTrigger) {
    t6 = [props.refetchTrigger];
    $[10] = props.refetchTrigger;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  useEffect(t5, t6);
  const [parent] = useAutoAnimate();
  let contentPermission = "canAccessFGDiscussions";
  if (["Article", "PodcastEpisode", "VideoLesson", "GroupRequest"].includes(props.attachmentType)) {
    contentPermission = null;
  }
  let t7;
  if ($[12] !== contentPermission || $[13] !== props.paywalledActionProps) {
    t7 = contentPermission ? {
      contentPermission,
      intent: "discussion-join",
      variant: "boxed",
      title: "Upgrade to join the discussion.",
      upgradeProductId: "scom-accelerator-tools",
      ...props.paywalledActionProps
    } : {
      contentPermission: "canAccessFGDiscussions",
      intent: "discussion-join",
      title: "Start a membership to join the discussion.",
      variant: "boxed",
      upgradeProductId: "scom-accelerator-tools",
      ...props.paywalledActionProps
    };
    $[12] = contentPermission;
    $[13] = props.paywalledActionProps;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const paywalledActionProps = t7;
  const t8 = props.invertOrder ? "column-reverse" : "column";
  let t9;
  if ($[15] !== paywalledActionProps || $[16] !== props.composerBoxProps || $[17] !== props.innerPadding || $[18] !== props.threadId || $[19] !== query || $[20] !== showTopComposer) {
    t9 = showTopComposer && <Box {...props.composerBoxProps || {
      padding: props.innerPadding
    }} width="full"><PaywalledAction contentPermission="canAccessFGDiscussions" intent="discussion-join" title="Start a Membership to join the discussion." variant="boxed" {...paywalledActionProps}><CommentComposer placeholder="Join the conversation..." newCommentValues={{
          threadId: props.threadId
        }} onSuccess={async () => {
          await query.refetch();
        }} /></PaywalledAction></Box>;
    $[15] = paywalledActionProps;
    $[16] = props.composerBoxProps;
    $[17] = props.innerPadding;
    $[18] = props.threadId;
    $[19] = query;
    $[20] = showTopComposer;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== props) {
    t10 = data => {
      if (data.forum?.comments?.items?.length < 1 && !props.parentCommentId) {
        return props.renderBlankState ? props.renderBlankState() : <Flex height="full" justify="center" paddingY={6} width="full"><Center><Text fontFamily="Poppins, sans-serif" fontWeight="600">No comments yet.</Text></Center></Flex>;
      }
    };
    $[22] = props;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== parent || $[25] !== props.bgColor || $[26] !== props.collapseComments || $[27] !== props.commentCardProps || $[28] !== props.innerPadding || $[29] !== props.isScrollable || $[30] !== props.maxHeight || $[31] !== props.showReplies || $[32] !== query) {
    t11 = data_0 => <Box height="full" overflowY="auto" width="full"><ScrollBoxWithShadows bgColor={props.bgColor} isScrollable={props.isScrollable} maxHeight={props.maxHeight}><Box ref={parent} overflow="hidden" padding={props.innerPadding}>{data_0.forum.comments.items.map(comment => <CommentCard {...props.commentCardProps} key={comment.commentId} comment={comment} isCollapsed={props.collapseComments} showReplies={props.showReplies} />)}{data_0.forum.comments.pagination.hasNextPage && <Button marginY="6px" width="full" onClick={() => {
            query.fetchMore({
              variables: {
                page: query.data.forum.comments.pagination.page + 1
              }
            });
          }}>Load More</Button>}</Box></ScrollBoxWithShadows></Box>;
    $[24] = parent;
    $[25] = props.bgColor;
    $[26] = props.collapseComments;
    $[27] = props.commentCardProps;
    $[28] = props.innerPadding;
    $[29] = props.isScrollable;
    $[30] = props.maxHeight;
    $[31] = props.showReplies;
    $[32] = query;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== query || $[35] !== t10 || $[36] !== t11) {
    t12 = <QueryContainer query={query} beforeRenderData={t10} renderData={t11} />;
    $[34] = query;
    $[35] = t10;
    $[36] = t11;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== paywalledActionProps || $[39] !== props.composerBoxProps || $[40] !== props.composerPlaceholder || $[41] !== props.innerPadding || $[42] !== props.threadId || $[43] !== query || $[44] !== showBottomComposer) {
    t13 = showBottomComposer && <Box {...props.composerBoxProps || {
      padding: props.innerPadding
    }} width="full"><PaywalledAction contentPermission="canAccessFGDiscussions" title="Start a Membership to join the discussion." variant="boxed" {...paywalledActionProps}><CommentComposer placeholder={props.composerPlaceholder || "Join the conversation..."} newCommentValues={{
          threadId: props.threadId
        }} onSuccess={() => {
          query.refetch();
        }} /></PaywalledAction></Box>;
    $[38] = paywalledActionProps;
    $[39] = props.composerBoxProps;
    $[40] = props.composerPlaceholder;
    $[41] = props.innerPadding;
    $[42] = props.threadId;
    $[43] = query;
    $[44] = showBottomComposer;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  let t14;
  if ($[46] !== paywalledActionProps || $[47] !== props.composerBoxProps || $[48] !== props.composerPlaceholder || $[49] !== props.innerPadding || $[50] !== props.threadId || $[51] !== query || $[52] !== showFixedComposer) {
    t14 = showFixedComposer && <Box {...props.composerBoxProps || {
      bgColor: "rgba(0,0,0,0.1)",
      padding: props.innerPadding
    }} bottom={0} left={0} position="sticky" right={0} width="full"><PaywalledAction contentPermission="canAccessFGDiscussions" title="Start a Membership to join the discussion." variant="boxed" {...paywalledActionProps}><CommentComposer placeholder={props.composerPlaceholder || "Join the conversation..."} newCommentValues={{
          threadId: props.threadId
        }} onSuccess={() => {
          query.refetch();
        }} /></PaywalledAction></Box>;
    $[46] = paywalledActionProps;
    $[47] = props.composerBoxProps;
    $[48] = props.composerPlaceholder;
    $[49] = props.innerPadding;
    $[50] = props.threadId;
    $[51] = query;
    $[52] = showFixedComposer;
    $[53] = t14;
  } else {
    t14 = $[53];
  }
  let t15;
  if ($[54] !== t12 || $[55] !== t13 || $[56] !== t14 || $[57] !== t8 || $[58] !== t9) {
    t15 = <VStack direction={t8} gap={1} height="full" position="relative" width="full">{t9}{t12}{t13}{t14}</VStack>;
    $[54] = t12;
    $[55] = t13;
    $[56] = t14;
    $[57] = t8;
    $[58] = t9;
    $[59] = t15;
  } else {
    t15 = $[59];
  }
  return t15;
};
CommentList.queries = {
  comments: () => gql`
    query(
      $order: [[String]]
      $page: Int
      $pageSize: Int
      $parentCommentId: String
      $threadId: String
    ) {
      forum {
        id
        comments(
          order: $order
          pageSize: $pageSize
          page: $page
          parentCommentId: $parentCommentId
          threadId: $threadId
        ) {
          id
          items {
            id
            ${CommentCard.fragments.ScoComment()}
          }
          pagination {
            itemCount
            hasNextPage
            page
            pageSize
            pageCount
          }
        }
      }
    }
  `
};
CommentList.fragments = {};